import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  useMediaQuery
} from '@material-ui/core';
import moment from 'moment';

import * as actions from '../../../store/actions/index';
import Spinner from '../../UI/Spinner/Spinner';
import EstadoComprobanteTag from '../EstadoComprobanteTag/EstadoComprobanteTag';
import RenglonesComprobanteTable, { useTablaComprobanteStyles } from '../RenglonesComprobanteTable/RenglonesComprobanteTable';
import useDatosComprobante from '../hooks/useDatosComprobante';
import { formatNumber, formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';
import { texto, numeroComprobante, tipoComprobante, clienteComprobante } from '../textosComprobante';

const useStyles = makeStyles(() => ({
  content: {
    minHeight: '40vh'
  },
  cabecera: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    marginBottom: '10px'
  },
  mensaje: {
    padding: '20px',
    textAlign: 'center'
  }
}));

/**
 * Detalle de un comprobante de ventas: renglones con lo pedido, descargado y pendiente (Vnt_Detalle) y los
 * comprobantes que lo descargaron (Vnt_Descarga). Se pide al abrir.
 */
const ImpresionDetalleModal = ({ comprobante, onClose }) => {
  const classes = useStyles();
  const tabla = useTablaComprobanteStyles();
  // En celular el modal ocupa toda la pantalla.
  const pantallaCompleta = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const { datos: detalle, error } = useDatosComprobante(comprobante, actions.loadDetalleComprobanteImpresion);

  const tablaDescargas =
    detalle && detalle.descargas.length > 0 ? (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={tabla.header}>Fecha</TableCell>
              <TableCell className={tabla.header}>Comprobante</TableCell>
              <TableCell className={tabla.header}>Núm. Externo</TableCell>
              <TableCell className={tabla.header}>Artículo</TableCell>
              <TableCell className={tabla.header} align="right">Cantidad</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detalle.descargas.map((descarga, index) => (
              <TableRow key={`${descarga.idComproba}-${descarga.nroReferencia}-${descarga.renglon}-${index}`}>
                <TableCell className={tabla.celda}>{descarga.fecha ? moment(descarga.fecha).format('DD/MM/YYYY') : ''}</TableCell>
                <TableCell className={tabla.celda}>
                  {tipoComprobante(descarga)}
                  {descarga.anulado && <span style={{ color: '#d32f2f', fontWeight: 'bold' }}> (Anulado)</span>}
                </TableCell>
                <TableCell className={tabla.celda}>{numeroComprobante(descarga)}</TableCell>
                <TableCell className={tabla.celda}>{texto(descarga.idArticulo)}</TableCell>
                <TableCell className={tabla.celda} align="right">{formatNumber(descarga.cantidad)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    ) : (
      <Typography variant="body2" style={{ color: 'gray' }}>
        Sin descargas
      </Typography>
    );

  return (
    <Dialog open={!!comprobante} onClose={onClose} fullWidth fullScreen={pantallaCompleta} maxWidth="lg">
      <DialogTitle>{comprobante ? `${tipoComprobante(comprobante)} ${numeroComprobante(comprobante)}` : ''}</DialogTitle>
      <DialogContent dividers className={classes.content}>
        {comprobante && (
          <div className={classes.cabecera}>
            <Typography variant="body2">
              {`${clienteComprobante(comprobante)} · ${moment(comprobante.fecha).format('DD/MM/YYYY')} · Total ${formatNumberWithMonedaSigno(comprobante.total)}`}
            </Typography>
            <div>
              <EstadoComprobanteTag comprobante={{ ...comprobante, ultimaAccion: null }} />
            </div>
          </div>
        )}
        {error ? (
          <Typography className={classes.mensaje} color="error">
            {error}
          </Typography>
        ) : detalle ? (
          <React.Fragment>
            <RenglonesComprobanteTable renglones={detalle.renglones} />
            <div className={tabla.seccion}>
              <Typography variant="subtitle2">Descargas</Typography>
            </div>
            {tablaDescargas}
          </React.Fragment>
        ) : (
          <Spinner />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImpresionDetalleModal;
